import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Section } from '../shared';
import { PayPalCheckoutModal } from '../shared/PayPalCheckout';

// Animated Checkmark Component
const AnimatedCheckmark = ({ delay, isInView }) => {
  return (
    <motion.svg 
      className="w-5 h-5 text-neon-orange flex-shrink-0" 
      viewBox="0 0 20 20"
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <motion.path
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        fill="currentColor"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          visible: { pathLength: 1, opacity: 1 }
        }}
        transition={{ duration: 0.5, delay: delay * 0.1 }}
      />
    </motion.svg>
  );
};

// Billing Toggle Component
const BillingToggle = ({ annual, setAnnual }) => {
  return (
    <div className="flex items-center justify-center gap-4 mt-8">
      <span className={`text-sm transition-colors ${!annual ? 'text-white' : 'text-gray-500'}`}>
        Monthly
      </span>
      <motion.button 
        className="w-16 h-8 bg-dark-base rounded-full relative border border-neon-orange/20"
        onClick={() => setAnnual(!annual)}
        whileTap={{ scale: 0.95 }}
      >
        <motion.div 
          className="w-6 h-6 bg-neon-orange rounded-full absolute top-1"
          animate={{ x: annual ? 34 : 4 }}
          transition={{ type: "spring", stiffness: 500, damping: 30 }}
        />
      </motion.button>
      <span className={`text-sm transition-colors ${annual ? 'text-white' : 'text-gray-500'}`}>
        Annual
      </span>
      <motion.span 
        className="text-neon-orange text-sm font-semibold bg-neon-orange/10 px-3 py-1 rounded-full"
        animate={{ scale: annual ? [1, 1.1, 1] : 1 }}
        transition={{ duration: 0.3 }}
      >
        Save 30%
      </motion.span>
    </div>
  );
};

// Pricing Card Component
const PricingCard = ({ plan, annual, index, onCheckout }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const [isHovered, setIsHovered] = useState(false);

  const price = annual ? plan.annualPrice : plan.monthlyPrice;

  return (
    <motion.div
      ref={ref}
      className={`relative rounded-2xl overflow-hidden ${
        plan.highlighted ? 'z-10' : ''
      }`}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay: index * 0.15 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Popular Badge */}
      {plan.highlighted && (
        <motion.div 
          className="absolute -top-1 left-1/2 transform -translate-x-1/2 px-4 py-1 bg-neon-orange text-white text-sm font-bold rounded-b-lg z-20"
          animate={{
            y: [0, -2, 0],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Most Popular
        </motion.div>
      )}

      {/* Card Container */}
      <motion.div
        className={`p-8 h-full ${
          plan.highlighted 
            ? 'bg-gradient-to-b from-neon-orange/20 via-dark-deeper to-dark-deeper border-2 border-neon-orange' 
            : 'bg-dark-base/50 border border-neon-orange/10'
        }`}
        animate={plan.highlighted ? {
          boxShadow: isHovered 
            ? '0 0 60px rgba(253, 128, 46, 0.4)' 
            : '0 0 30px rgba(253, 128, 46, 0.2)',
          scale: isHovered ? 1.02 : 1,
        } : {
          borderColor: isHovered ? 'rgba(253, 128, 46, 0.3)' : 'rgba(253, 128, 46, 0.1)'
        }}
        transition={{ duration: 0.3 }}
      >
        {/* Plan Name */}
        <h3 className="text-xl font-heading font-bold text-white mb-2">{plan.name}</h3>
        <p className="text-gray-500 text-sm mb-6">{plan.description}</p>

        {/* Price */}
        <div className="mb-6">
          <motion.div
            key={price}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-baseline"
          >
            <span className="text-4xl font-display font-bold text-white">${price}</span>
            <span className="text-gray-500 ml-1">/month</span>
          </motion.div>
          {annual && (
            <motion.p 
              className="text-neon-orange text-sm mt-1"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              Billed ${price * 12}/year
            </motion.p>
          )}
        </div>

        {/* Features List */}
        <ul className="space-y-3 mb-8">
          {plan.features.map((feature, idx) => (
            <motion.li 
              key={idx} 
              className="flex items-start gap-3 text-gray-300"
              initial={{ opacity: 0, x: -10 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: idx * 0.1 + 0.3 }}
            >
              <AnimatedCheckmark delay={idx} isInView={isInView} />
              <span className="text-sm">{feature}</span>
            </motion.li>
          ))}
        </ul>

        {/* CTA Button */}
        <motion.button
          onClick={() => onCheckout(plan)}
          className={`w-full py-3 rounded-full font-bold transition-all ${
            plan.highlighted 
              ? 'bg-neon-orange text-white' 
              : 'border-2 border-neon-orange text-neon-orange hover:bg-neon-orange hover:text-white'
          }`}
          whileHover={{ 
            scale: 1.02,
            boxShadow: plan.highlighted ? '0 0 30px rgba(253, 128, 46, 0.5)' : 'none'
          }}
          whileTap={{ scale: 0.98 }}
        >
          {plan.cta}
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

// Comparison Modal Component
const ComparisonModal = ({ isOpen, onClose, plans }) => {
  const allFeatures = [
    { name: "Processing Hours", values: ["5 hours/mo", "Unlimited", "Unlimited"] },
    { name: "Noise Reduction", values: ["Basic", "Advanced AI", "Advanced AI"] },
    { name: "Export Formats", values: ["Standard", "All Formats", "All Formats"] },
    { name: "AI Voice Enhancement", values: ["—", "✓", "✓"] },
    { name: "Stem Separation", values: ["—", "✓", "✓"] },
    { name: "Batch Processing", values: ["Up to 10", "Up to 100", "Unlimited"] },
    { name: "API Access", values: ["—", "✓", "✓"] },
    { name: "Custom Workflows", values: ["—", "✓", "✓"] },
    { name: "Priority Support", values: ["—", "✓", "✓"] },
    { name: "Dedicated Support", values: ["—", "—", "✓"] },
    { name: "Custom Integrations", values: ["—", "—", "✓"] },
    { name: "SLA Guarantee", values: ["—", "—", "99.9%"] },
    { name: "Team Management", values: ["—", "—", "✓"] },
    { name: "White-label Options", values: ["—", "—", "✓"] },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Modal Content */}
          <motion.div
            className="relative bg-dark-deeper border border-neon-orange/20 rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-neon-orange/10">
              <h3 className="text-xl font-heading font-bold text-white">Compare All Plans</h3>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-dark-base flex items-center justify-center text-gray-400 hover:text-white hover:bg-neon-orange/20 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Table */}
            <div className="overflow-auto max-h-[calc(80vh-100px)]">
              <table className="w-full">
                <thead className="sticky top-0 bg-dark-deeper">
                  <tr>
                    <th className="text-left p-4 text-gray-400 font-normal">Feature</th>
                    {plans.map((plan, i) => (
                      <th 
                        key={i} 
                        className={`text-center p-4 ${
                          plan.highlighted ? 'text-neon-orange' : 'text-white'
                        }`}
                      >
                        {plan.name}
                        {plan.highlighted && (
                          <span className="block text-xs text-neon-orange/70 font-normal">Popular</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allFeatures.map((feature, i) => (
                    <tr 
                      key={i}
                      className="border-t border-neon-orange/5 hover:bg-neon-orange/5 transition-colors"
                    >
                      <td className="p-4 text-gray-300">{feature.name}</td>
                      {feature.values.map((value, j) => (
                        <td 
                          key={j} 
                          className={`text-center p-4 ${
                            value === '✓' ? 'text-neon-orange' : 
                            value === '—' ? 'text-gray-600' : 'text-gray-300'
                          }`}
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const PricingSection = () => {
  const [annual, setAnnual] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  
  const handleCheckout = (plan) => {
    setSelectedPlan(plan);
    setShowCheckout(true);
  };
  
  const plans = [
    {
      name: "Starter",
      monthlyPrice: 29,
      annualPrice: 19,
      description: "Perfect for individuals and small projects",
      features: [
        "5 hours of processing/month",
        "Basic noise reduction",
        "Standard export formats",
        "Email support",
        "Up to 10 files per batch"
      ],
      cta: "Start Free Trial",
      highlighted: false
    },
    {
      name: "Professional",
      monthlyPrice: 69,
      annualPrice: 49,
      description: "For serious creators and small teams",
      features: [
        "Unlimited processing",
        "Advanced AI noise reduction",
        "All export formats",
        "Priority support",
        "API access",
        "Custom workflows",
        "Up to 100 files per batch"
      ],
      cta: "Start Free Trial",
      highlighted: true
    },
    {
      name: "Enterprise",
      monthlyPrice: 199,
      annualPrice: 149,
      description: "For studios and large organizations",
      features: [
        "Everything in Professional",
        "Dedicated account manager",
        "Custom integrations",
        "99.9% SLA guarantee",
        "Team management",
        "White-label options",
        "Unlimited batch processing"
      ],
      cta: "Start Free Trial",
      highlighted: false
    },
  ];

  return (
    <Section id="pricing" className="bg-dark-deeper relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-1/4 -left-32 w-64 h-64 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 6, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-32 w-96 h-96 bg-neon-orange/5 rounded-full blur-3xl"
          animate={{ scale: [1.3, 1, 1.3], opacity: [0.2, 0.3, 0.2] }}
          transition={{ duration: 7, repeat: Infinity }}
        />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-8">
          <motion.span 
            className="text-neon-orange font-semibold text-sm uppercase tracking-wider"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Pricing
          </motion.span>
          
          <motion.h2 
            className="text-3xl md:text-4xl font-heading font-bold text-white mt-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Simple, Transparent{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-orange to-neon-orange-light">
              Pricing
            </span>
          </motion.h2>
          
          <motion.p 
            className="text-gray-400 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Start with a 14-day free trial. No credit card required.
          </motion.p>

          {/* Billing Toggle */}
          <BillingToggle annual={annual} setAnnual={setAnnual} />
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8 mt-12">
          {plans.map((plan, i) => (
            <PricingCard 
              key={i} 
              plan={plan} 
              annual={annual} 
              index={i}
              onCheckout={handleCheckout}
            />
          ))}
        </div>

        {/* Compare All Button */}
        <motion.div 
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <motion.button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 text-neon-orange border border-neon-orange/30 px-6 py-3 rounded-full hover:bg-neon-orange/10 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Compare All Features
          </motion.button>
        </motion.div>

        
      </div>

      {/* Comparison Modal */}
      <ComparisonModal 
        isOpen={showModal} 
        onClose={() => setShowModal(false)}
        plans={plans}
      />

      {/* PayPal Checkout Modal */}
      {selectedPlan && (
        <PayPalCheckoutModal
          isOpen={showCheckout}
          onClose={() => {
            setShowCheckout(false);
            setSelectedPlan(null);
          }}
          plan={selectedPlan}
          annual={annual}
        />
      )}
    </Section>
  );
};

export default PricingSection;
